'use client';

import type { ReactNode } from 'react';

import { DesktopSearchBar } from '#/components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from '#/components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from '#/components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from '#/components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from '#/components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import type { LocationRadiusSelectHandle } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { LocationRadiusSelect } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { FlashesContainer } from '#/containers/FlashesContainer';
import { LayoutHeaderDefaultSearchTypeContext } from '#/layouts/Layout/Header/LayoutHeaderDefaultSearchTypeContext';
import { MobileSearchV2 } from '#/layouts/Layout/Header/MobileSearchV2/MobileSearchV2';
import { useLayoutHeaderSearch } from '#/layouts/Layout/Header/useLayoutHeaderSearch';
import { SearchRefinementsWrapper } from '#/modules/search/components/Refinements/SearchRefinementsWrapper';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';

import {
  LayoutHeaderDesktopSearchHolder,
  LayoutHeaderSearchSubmenuHolder,
} from './LayoutHeaderSearchSubmenu.styled';

type Props = {
  defaultSearchType: SearchType;
  searchFromPageType: SearchFromPageType | undefined;
  showDesktopSearch: boolean;
  hideMobileSearch: boolean;
  isSearchLandingPage: boolean;
  showSearchRefinements: boolean;
  searchRefinements: ReactNode;
  isSearchPage: boolean;
  mobileSearchIcon: boolean;
};

export function LayoutHeaderSearchSubmenu({
  defaultSearchType,
  searchFromPageType,
  showDesktopSearch,
  hideMobileSearch,
  isSearchLandingPage,
  showSearchRefinements,
  searchRefinements,
  isSearchPage,
  mobileSearchIcon,
}: Props) {
  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({ isSearchLandingPage });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isSearchPage
    ? { type: 'controlled', onChange: setSearchUrlRadius, radius }
    : { type: 'uncontrolled', radius };

  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType,
    isSearchPage,
    isSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  return (
    <LayoutHeaderSearchSubmenuHolder>
      {showDesktopSearch && (
        <LayoutHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
          <DesktopSearchBar
            defaultSearchType={defaultSearchType}
            onSearch={search}
            searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
            locationInput={
              <DesktopSearchBarLocationInput
                searchLocation={searchLocation ?? null}
                onChangeSearchLocation={changeSearchLocation}
              />
            }
            radiusInput={
              <LocationRadiusSelect
                handle={radiusHandle}
                options={radiusOptions}
                disabled={
                  !searchLocation || !isPointGeotype(searchLocation.geoType)
                }
                qaId="page-header-desktop-location-radius"
              />
            }
            queryInput={
              <DesktopSearchBarQueryInput
                initialQuery={initialQuery}
                searchOnClear={searchOnClear}
              />
            }
            submitButton={<DesktopSearchBarSubmitButton />}
          />
        </LayoutHeaderDesktopSearchHolder>
      )}

      {!hideMobileSearch && !mobileSearchIcon && (
        <MobileSearchV2
          trigger="pill"
          defaultSearchType={defaultSearchType}
          isSearchPage={isSearchPage}
          isSearchLandingPage={isSearchLandingPage}
          initialQuery={initialQuery}
          searchLocation={searchLocation}
          onChangeSearchLocation={changeSearchLocation}
          radiusOptions={radiusOptions}
          radiusHandle={radiusHandle}
          onSearch={search}
          searchOnClear={searchOnClear}
        />
      )}

      {showSearchRefinements && (
        <SearchRefinementsWrapper hideOnMobile sticky>
          <LayoutHeaderDefaultSearchTypeContext value={defaultSearchType}>
            {searchRefinements}
          </LayoutHeaderDefaultSearchTypeContext>
        </SearchRefinementsWrapper>
      )}
      <FlashesContainer />
    </LayoutHeaderSearchSubmenuHolder>
  );
}
